import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Input, 
  Select, 
  Typography,
  Alert,
  Radio,
  message,
  Switch,
  Popconfirm
} from 'antd';
import { FiEdit, FiSave, FiX, FiCheck } from 'react-icons/fi';
import { RefreshIcon } from '../common/ActionIcons';
import { IoLocationSharp } from "react-icons/io5";
import { useTranslation } from 'react-i18next';
import dayjs from '../../utils/dayjs';
import { filterBuilder, removeFilter } from '../../utils/functions';

import { 
  getLocationSubjects 
} from '../../actions/locationActions';
import { 
  updateSubject,
  listSubjects
} from '../../actions/subjectActions';
import SubjectDetails from '../patient/subject/SubjectDetails.component';
import ApiErrorAlert from '../../components/common/ApiErrorAlert';

const { Text } = Typography;

const SubjectsByLocationExplorer = ({ 
  selectedLocationId, 
  t,
  filterByUploadBatch = true,
  uploadBatchId
}) => {
  const dispatch = useDispatch();
  
  // Redux state - use different states based on filter
  const locationSubjects = useSelector(state => state.locationSubjects);
  const allSubjects = useSelector(state => state.subjectList);
  
  const subjectList = filterByUploadBatch ? locationSubjects : allSubjects;
  const { loading: loadingSubjects, subjects = [], error: errorSubjects, currentPage, totalPages, totalCount, limit } = subjectList;

  const subjectUpdate = useSelector(state => state.subjectUpdate);
  const { loading: loadingUpdate, success: successUpdate, error: errorUpdate, subject: updatedSubject } = subjectUpdate;

  // Local state
  const [filter, setFilter] = useState({
    query: '',
    filter: 'subjectIdentifier',
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [searchText, setSearchText] = useState('');
  const [searchProp, setSearchProp] = useState('subjectIdentifier');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [localSubjects, setLocalSubjects] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // Load subjects when location changes or filter changes
  useEffect(() => {
    if (filterByUploadBatch && selectedLocationId) {
      // Load subjects for specific location within upload batch
      dispatch(getLocationSubjects(selectedLocationId, filter));
    } else if (!filterByUploadBatch && selectedLocationId) {
      // Load all subjects for the selected location (not limited to upload batch)
      dispatch(listSubjects(filter));
    }
  }, [dispatch, selectedLocationId, filter, filterByUploadBatch]);

  // Update filter when filterByUploadBatch or uploadBatchId changes
  useEffect(() => {
    if (filterByUploadBatch && uploadBatchId) {
      setFilter(prevFilter => filterBuilder(prevFilter, 'uploadBatchId', uploadBatchId, { 
        combine: false, 
        resetPage: true 
      }));
    } else if (!filterByUploadBatch && selectedLocationId) {
      // Filter by locationId when in "All Locations" mode
      setFilter(prevFilter => filterBuilder(prevFilter, 'locationId', selectedLocationId, { 
        combine: false, 
        resetPage: true 
      }));
    } else {
      setFilter(prevFilter => filterBuilder(prevFilter, 'subjectIdentifier', '', { 
        combine: false, 
        resetPage: true 
      }));
    }
  }, [filterByUploadBatch, uploadBatchId, selectedLocationId]);

  // Update local subjects when Redux subjects change
  useEffect(() => {
    if (subjects) {
      setLocalSubjects(subjects);
    }
  }, [subjects]);



  // Handle update success
  useEffect(() => {
    if (successUpdate && editingRowId && updatedSubject) {
      setLocalSubjects(prevSubjects => 
        prevSubjects.map(subject => 
          subject.id === updatedSubject.id ? { ...subject, ...updatedSubject } : subject
        )
      );
      message.success(t('Subject updated successfully'));
      setEditingRowId(null);
      setEditingData({});
      dispatch({ type: 'SUBJECT_UPDATE_RESET' });
    }
  }, [successUpdate, editingRowId, t, updatedSubject, dispatch]);

  // Handle enrollment success
  useEffect(() => {
    if (successUpdate && !editingRowId && updatedSubject) {
      setLocalSubjects(prevSubjects => 
        prevSubjects.map(subject => 
          subject.id === updatedSubject.id ? { ...subject, ...updatedSubject } : subject
        )
      );
      message.success(t('Subject enrolled successfully'));
      dispatch({ type: 'SUBJECT_UPDATE_RESET' });
    }
  }, [successUpdate, editingRowId, t, updatedSubject, dispatch]);

  // Handle errors
  useEffect(() => {
    if (errorUpdate && editingRowId) {
      message.error(Array.isArray(errorUpdate) ? errorUpdate.join(', ') : errorUpdate);
      dispatch({ type: 'SUBJECT_UPDATE_RESET' });
    }
  }, [errorUpdate, editingRowId, t, dispatch]);

  // Handlers
  const handleRefresh = useCallback(() => {
    if (filterByUploadBatch && selectedLocationId) {
      dispatch(getLocationSubjects(selectedLocationId, filter));
    } else if (!filterByUploadBatch && selectedLocationId) {
      dispatch(listSubjects(filter));
    }
  }, [dispatch, selectedLocationId, filter, filterByUploadBatch]);

  const handleEdit = useCallback((record) => {
    setEditingRowId(record.id);
    setEditingData({ ...record });
  }, []);

  const handleSave = useCallback(() => {
    if (editingRowId && editingData) {
      const currentSubject = localSubjects.find(s => s.id === editingRowId);
      const changes = {};
      Object.keys(editingData).forEach(key => {
        if (editingData[key] !== currentSubject[key]) {
          changes[key] = editingData[key];
        }
      });

      if (Object.keys(changes).length > 0) {
        dispatch(updateSubject(editingRowId, changes));
      } else {
        setEditingRowId(null);
        setEditingData({});
      }
    }
  }, [editingRowId, editingData, localSubjects, dispatch]);

  const handleCancel = useCallback(() => {
    setEditingRowId(null);
    setEditingData({});
  }, []);

  const handleInputChange = useCallback((field, value) => {
    setEditingData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleTableChange = useCallback((pagination, filters, sorter) => {
    setFilter(prev => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
      sortBy: sorter.field || 'createdAt',
      sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc'
    }));
  }, []);

  // Handler for enrolling a subject
  const handleEnroll = useCallback((record) => {
    const enrollmentData = {
      enrollmentStatus: 'ENROLLED',
      enrollmentDate: new Date().toISOString(),
      isSelectedForSubmission: true
    };
    dispatch(updateSubject(record.id, enrollmentData));
  }, [dispatch]);

  const handleSearch = (value) => {
    setSearchText(value);
    setFilter(prev => filterBuilder(prev, searchProp, value, { 
      combine: true, 
      resetPage: true 
    }));
  };

  const handleSearchPropChange = (value) => {
    setSearchProp(value);
    if (searchText) {
      setFilter(prev => filterBuilder(prev, value, searchText, { 
        combine: true, 
        resetPage: true 
      }));
    }
  };

  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    if (value === 'all') {
      // Remove status filter but keep other filters
      setFilter(prev => removeFilter(prev, 'status', { resetPage: true }));
    } else {
      // First remove any existing status filter, then add the new one
      setFilter(prev => {
        const withoutStatus = removeFilter(prev, 'status', { resetPage: false });
        return filterBuilder(withoutStatus, 'status', value, { 
          combine: true, 
          resetPage: true 
        });
      });
    }
  };

  const handleReset = () => {
    setSearchText('');
    setSearchProp('subjectIdentifier');
    setStatusFilter('all');
    const baseFilter = {
      page: 1,
      limit: 10,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    };
    
    if (filterByUploadBatch && uploadBatchId) {
      setFilter(filterBuilder(baseFilter, 'uploadBatchId', uploadBatchId, { 
        combine: false, 
        resetPage: true 
      }));
    } else if (!filterByUploadBatch && selectedLocationId) {
      setFilter(filterBuilder(baseFilter, 'locationId', selectedLocationId, { 
        combine: false, 
        resetPage: true 
      }));
    } else {
      setFilter(filterBuilder(baseFilter, 'subjectIdentifier', '', { 
        combine: false, 
        resetPage: true 
      }));
    }
  };

  // Search and filter options
  const searchOptions = [
    { value: 'subjectIdentifier', label: t('Subject ID') },
    { value: 'patientId', label: t('Patient ID') },
    { value: 'postcode', label: t('Postcode') },
    { value: 'studyRole', label: t('Study Role') },
    { value: 'enrollmentStatus', label: t('Enrollment Status') },
    { value: 'approvalStatus', label: t('Approval Status') }
  ];

  const statusOptions = [
    { label: t('All'), value: 'all' },
    { label: t('Approved'), value: 'APPROVED' },
    { label: t('Rejected'), value: 'REJECTED' },
    { label: t('Pending Review'), value: 'PENDING_REVIEW' },
    { label: t('Pending'), value: 'PENDING' },
    { label: t('Provisionally Approved'), value: 'PROVISIONALLY_APPROVED' }
  ];

  // Helper functions
  const getStatusTag = (status) => {
    const statusColors = {
      'APPROVED': 'green',
      'REJECTED': 'red',
      'PENDING': 'orange',
      'PENDING_REVIEW': 'orange',
      'PROVISIONALLY_APPROVED': 'blue',
      'ENROLLED': 'green'
    };
    
    return (
      <Tag color={statusColors[status] || 'default'}>
        {status}
      </Tag>
    );
  };

  // Expandable row configuration
  const expandedRowRender = (record) => {
    return <SubjectDetails subjectId={record.id} />;
  };

  const expandable = {
    expandedRowRender,
    rowExpandable: (record) => true,
    expandedRowKeys,
    onExpandedRowsChange: setExpandedRowKeys,
  };

  // Table columns
  const columns = [
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'left',
      width: 160,
      render: (_, record) => {
        const isEditing = editingRowId === record.id;
        const isEnrolled = record.enrollmentStatus === 'ENROLLED';
        
        return (
          <Space size="small">
            {isEditing ? (
              <>
                <Button type="primary" size="small" icon={<FiSave />} onClick={handleSave} loading={loadingUpdate} />
                <Button size="small" icon={<FiX />} onClick={handleCancel} />
              </>
            ) : (
              <>
                <Button type="text" size="small" icon={<FiEdit />} onClick={() => handleEdit(record)} />
                {!isEnrolled && (
                  <Popconfirm
                    title={t('Enroll Subject')}
                    description={t('Are you sure you want to enroll this subject?')}
                    onConfirm={() => handleEnroll(record)}
                    okText={t('Yes')}
                    cancelText={t('No')}
                  >
                    <Button 
                      type="text" 
                      size="small"
                      icon={<FiCheck />}
                      title={t('Enroll Subject')}
                    >
                      {t('Approve')}
                    </Button>
                  </Popconfirm>
                )}
              </>
            )}
          </Space>
        );
      }
    },
    {
      title: t('Subject ID'),
      key: 'subjectIdentifier',
      width: 220,
      render: (_, record) => {
        const isEditing = editingRowId === record.id;
        return (
          <div>
            {isEditing ? (
              <Input
                value={editingData.subjectIdentifier || record.subjectIdentifier}
                onChange={(e) => handleInputChange('subjectIdentifier', e.target.value)}
                size="small"
                placeholder="Subject ID"
              />
            ) : (
              <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                {record.subjectIdentifier}
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: t('System Status'),
      dataIndex: 'status',
      key: 'status',
      width: 220,
      render: (text, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Select
            value={editingData.status || text}
            onChange={(value) => handleInputChange('status', value)}
            size="small"
            style={{ width: '100%' }}
          >
            <Select.Option value="PROVISIONALLY_APPROVED">PROVISIONALLY_APPROVED</Select.Option>
            <Select.Option value="REJECTED">REJECTED</Select.Option>
            <Select.Option value="PENDING">PENDING</Select.Option>
            <Select.Option value="REVIEW">REVIEW</Select.Option>
          </Select>
        ) : (
          getStatusTag(text)
        );
      }
    },
    {
      title: t('Enrollment Status'),
      dataIndex: 'enrollmentStatus',
      key: 'enrollmentStatus',
      width: 130,
      render: (text, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Select
            value={editingData.enrollmentStatus || text}
            onChange={(value) => handleInputChange('enrollmentStatus', value)}
            size="small"
            style={{ width: '100%' }}
          >
            <Select.Option value="ENROLLED">ENROLLED</Select.Option>
            <Select.Option value="REJECTED">REJECTED</Select.Option>
          </Select>
        ) : (
          getStatusTag(text)
        );
      }
    },
    {
      title: t('Selected'),
      dataIndex: 'isSelectedForSubmission',
      key: 'isSelectedForSubmission',
      width: 100,
      render: (isSelected, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Switch
            checked={editingData.isSelectedForSubmission !== undefined ? editingData.isSelectedForSubmission : isSelected}
            onChange={(checked) => handleInputChange('isSelectedForSubmission', checked)}
            size="small"
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {isSelected ? (
              <FiCheck style={{ color: '#52c41a', fontSize: '16px' }} />
            ) : (
              <FiX style={{ color: '#f5222d', fontSize: '16px' }} />
            )}
          </div>
        );
      }
    },
    {
      title: t('Enrollment Date'),
      dataIndex: 'enrollmentDate',
      key: 'enrollmentDate',
      width: 130,
      render: (date) => (
        <Text style={{ fontSize: '12px' }}>
          {date ? dayjs(date).format('DD/MM/YY HH:mm') : '-'}
        </Text>
      )
    },
    {
      title: t('Created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      render: (date) => (
        <Text style={{ fontSize: '12px' }}>
          {dayjs(date).format('DD/MM/YY HH:mm')}
        </Text>
      )
    }
  ];

  if (!selectedLocationId && filterByUploadBatch) {
    return (
      <Card
        title={
          <Space>
            <Text strong>{t('Subjects')}</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('Select a location to view subjects')}
            </Text>
          </Space>
        }
        size="small"
      >
        <div style={{ textAlign: 'center', padding: '40px', color: '#8c8c8c' }}>
          <IoLocationSharp style={{ fontSize: '48px', marginBottom: '16px' }} />
          <Text>{t('Please select a location from the list above to view location-specific subjects')}</Text>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <Text strong>{t('Subjects')}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            ({totalCount || localSubjects?.length || 0} {t('items')})
          </Text>
          {filterByUploadBatch && uploadBatchId && (
            <Tag color="blue" style={{ fontSize: '10px', marginLeft: '8px' }}>
              {t('Location Subjects from Upload Batch')}
            </Tag>
          )}
          {!filterByUploadBatch && (
            <Tag color="green" style={{ fontSize: '10px', marginLeft: '8px' }}>
              {t('All Location Subjects')}
            </Tag>
          )}
        </Space>
      }
      size="small"
      extra={
        <Button icon={<RefreshIcon />} onClick={handleRefresh} size="small">
          {t('Refresh')}
        </Button>
      }
    >
      <ApiErrorAlert error={errorSubjects} closable />

      {/* Search and Filter Controls */}
      <div style={{ marginBottom: 16 }}>
        <Space wrap style={{ marginBottom: 12 }}>
          <span style={{ fontSize: '12px', color: '#666' }}>{t('Search')}:</span>
          <Select
            placeholder={t("Search by")}
            style={{ width: 140 }}
            size="small"
            value={searchProp}
            onChange={handleSearchPropChange}
          >
            {searchOptions.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
          <Input.Search
            placeholder={`${t("Search by")} ${searchOptions.find(opt => opt.value === searchProp)?.label || ''}`}
            onSearch={handleSearch}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
            size="small"
          />
          <Button onClick={handleReset} size="small">
            {t("Reset")}
          </Button>
        </Space>

        <div>
          <Text style={{ fontSize: '12px', color: '#666', marginRight: 8 }}>{t('Status')}:</Text>
          <Radio.Group
            value={statusFilter}
            onChange={handleStatusFilterChange}
            size="small"
            buttonStyle="solid"
          >
            {statusOptions.map((option) => (
              <Radio.Button key={option.value} value={option.value}>
                {option.label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={localSubjects}
        rowKey="id"
        loading={loadingSubjects}
        size="small"
        pagination={{
          current: currentPage,
          pageSize: filter.limit,
          total: totalCount,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`,
          pageSizeOptions: ['10', '20', '50'],
          size: 'small'
        }}
        onChange={handleTableChange}
        expandable={expandable}
        scroll={{ x: 800, y: 300 }}
        bordered
      />
    </Card>
  );
};

export default SubjectsByLocationExplorer;
